import React, { useState, useEffect, useRef } from "react";
import SpreetailHeader from "../layout/SpreetailHeader";
import ProgressBar from "@ramonak/react-progress-bar";
import useRCAProgress from "../../hooks/useRCAProgress";
import { useAnalysisGuard } from "../../hooks/useAnalysisGuard";
import { getAvailableDataSources, extractTableData } from "../../../shared/excelDataExtractor";
import RCAResultsView from "./RCAResultsView";
import RCAPlanView from "./RCAPlanView";
import ColumnSemanticsView, { tableSemanticsToColumns } from "./ColumnSemanticsView";
import { generateRCAPlan, getColumnDescriptions, RCAPlan, PlanStep, ColumnSemantics, TableColumnSemantics, RCATableInput } from "../../../shared/rcaApiService";

type ViewState = "input" | "columns" | "plan" | "analyzing" | "results";

/**
 * Filters each table's data to only include columns that the user selected
 * in the column semantics view. Tables with no selected columns are removed.
 */
function filterTablesDataBySelectedColumns(
    tablesData: RCATableInput[],
    selectedColumnKeys: Set<string>
): RCATableInput[] {
    return tablesData.map((table, tableIndex) => {
        const selectedIndices = table.headers
            .map((_, colIdx) => selectedColumnKeys.has(`${tableIndex}-${colIdx}`) ? colIdx : -1)
            .filter(i => i !== -1);

        return {
            table_name: table.table_name,
            headers: selectedIndices.map(i => table.headers[i]),
            rows: table.rows.map(row => selectedIndices.map(i => row[i]))
        };
    }).filter(t => t.headers.length > 0);
}

const RootCauseAnalysis: React.FC = () => {
    const [viewState, setViewState] = useState<ViewState>("input");
    const [selectedSheets, setSelectedSheets] = useState<string[]>([""]);
    const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
    const [sheetOptions, setSheetOptions] = useState<string[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
    const [userQuestion, setUserQuestion] = useState<string>("");
    const [showValidation, setShowValidation] = useState<boolean>(false);
    const [dataError, setDataError] = useState<string>("");
    const errorRef = useRef<HTMLDivElement>(null);

    // Plan-related state
    const [currentPlan, setCurrentPlan] = useState<RCAPlan | null>(null);
    const [originalPlan, setOriginalPlan] = useState<RCAPlan | null>(null);
    const [isGeneratingPlan, setIsGeneratingPlan] = useState<boolean>(false);
    const [tablesData, setTablesData] = useState<RCATableInput[]>([]);
    // Filtered tables data containing only user-selected columns
    const [filteredTablesData, setFilteredTablesData] = useState<RCATableInput[]>([]);

    // Column semantics state (table-based for accordion UI)
    const [tableSemantics, setTableSemantics] = useState<TableColumnSemantics[]>([]);
    const [selectedColumnKeys, setSelectedColumnKeys] = useState<Set<string>>(new Set());
    const [isLoadingDescriptions, setIsLoadingDescriptions] = useState<boolean>(false);

    const {
        progress,
        message,
        result,
        isLoading,
        error,
        startAnalysis,
        reset
    } = useRCAProgress();

    useEffect(() => {
        if ((error || dataError) && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [error, dataError]);

    // Guard: block taskpane auto-routing during any RCA busy phase
    const { setAnalysisInProgress } = useAnalysisGuard();
    useEffect(() => {
        const busy = isLoadingDescriptions || isGeneratingPlan || isLoading;
        setAnalysisInProgress(busy);
        return () => setAnalysisInProgress(false);
    }, [isLoadingDescriptions, isGeneratingPlan, isLoading, setAnalysisInProgress]);

    // Load available sheets on mount
    useEffect(() => {
        loadSheetOptions();
    }, []);

    // v2.3.0: Handle validation/analysis errors - go back to plan view to show the error
    useEffect(() => {
        if (error && viewState === "analyzing") {
            console.log("📋 Analysis/validation error, returning to plan view:", error);
            setViewState("plan");
        }
    }, [error, viewState]);

    const loadSheetOptions = async () => {
        setIsLoadingOptions(true);
        try {
            const sources = await getAvailableDataSources();
            setSheetOptions(sources);
        } catch (err) {
            console.error("Failed to load data sources:", err);
            setSheetOptions([]);
        } finally {
            setIsLoadingOptions(false);
        }
    };

    const handleSheetSelect = (index: number, option: string) => {
        setSelectedSheets(prev => {
            const updated = [...prev];
            updated[index] = option;
            return updated;
        });
        setDropdownOpen(null);
        setShowValidation(false);
    };

    const handleAddTable = () => {
        if (selectedSheets.length < 4) {
            setSelectedSheets(prev => [...prev, ""]);
        }
    };

    const handleRemoveTable = (index: number) => {
        setSelectedSheets(prev => prev.filter((_, i) => i !== index));
    };

    // Filter out already-selected sheets from dropdown options
    const getAvailableOptions = (currentIndex: number) => {
        const selectedByOthers = selectedSheets.filter((s, i) => i !== currentIndex && s);
        return sheetOptions.filter(opt => !selectedByOthers.includes(opt));
    };

    // Handle "Next" button - extract data and go to column semantics view
    const handleNext = async () => {
        // Validate inputs
        if (!userQuestion.trim()) {
            setShowValidation(true);
            return;
        }

        const validSheets = selectedSheets.filter(s => s.trim());
        if (validSheets.length === 0) {
            setShowValidation(true);
            return;
        }

        setDataError("");
        setShowValidation(false);

        try {
            // Extract data from all selected sheets
            const extractedTables: RCATableInput[] = [];

            for (const sheetName of validSheets) {
                const extracted = await extractTableData(sheetName);
                if (extracted.headers.length === 0 || extracted.data.length === 0) {
                    setDataError(`No valid data found in sheet "${sheetName}".`);
                    return;
                }
                extractedTables.push({
                    table_name: sheetName,
                    headers: extracted.headers,
                    rows: extracted.data
                });
            }

            setTablesData(extractedTables);

            console.log("📊 Data extracted from", extractedTables.length, "table(s)");

            // Show loading state on Next button
            setIsLoadingDescriptions(true);

            try {
                // Get column descriptions for each table sequentially
                const allTableSemantics: TableColumnSemantics[] = [];

                for (const table of extractedTables) {
                    const sampleRows = table.rows.slice(0, 10);
                    const descriptionResponse = await getColumnDescriptions({
                        headers: table.headers,
                        sample_rows: sampleRows
                    });

                    const columns: ColumnSemantics[] = descriptionResponse.descriptions.map(desc => ({
                        columnName: desc.column_name,
                        meaning: desc.meaning
                    }));

                    allTableSemantics.push({
                        tableName: table.table_name,
                        columns
                    });
                }

                setTableSemantics(allTableSemantics);
                console.log("✅ Column descriptions fetched for", allTableSemantics.length, "table(s)");
                setViewState("columns");
            } catch (descError) {
                console.error("❌ Failed to fetch column descriptions:", descError);
                setDataError(descError instanceof Error ? descError.message : "Failed to generate column descriptions. Please try again.");
            } finally {
                setIsLoadingDescriptions(false);
            }
        } catch (err) {
            console.error("Failed to extract data:", err);
            setDataError(err instanceof Error ? err.message : "Failed to extract data. Please try again.");
        }
    };

    // Handle "Generate Plan" from column semantics view
    const handleGeneratePlan = async () => {
        if (tablesData.length === 0) {
            setDataError("Missing table data. Please go back and try again.");
            return;
        }

        setIsGeneratingPlan(true);
        setDataError("");

        try {
            // Filter table semantics to only include selected (checked) columns
            const filteredTableSemantics = tableSemantics.map((table, tableIndex) => ({
                ...table,
                columns: table.columns.filter((_, columnIndex) =>
                    selectedColumnKeys.has(`${tableIndex}-${columnIndex}`)
                )
            })).filter(table => table.columns.length > 0);

            // Flatten filtered table semantics to column semantics for the API
            const flatColumnSemantics = tableSemanticsToColumns(filteredTableSemantics);

            // Filter tables data to only include user-selected columns
            const filtered = filterTablesDataBySelectedColumns(
                tablesData, selectedColumnKeys
            );
            setFilteredTablesData(filtered);

            console.log("📊 Generating RCA plan with:", {
                tables: filtered.length,
                question: userQuestion.substring(0, 50) + "...",
                columnSemantics: flatColumnSemantics.length
            });

            // Generate the plan with multi-table format
            const planResponse = await generateRCAPlan({
                tables: filtered,
                user_question: userQuestion,
                column_semantics: flatColumnSemantics.length > 0 ? flatColumnSemantics : undefined
            });

            console.log("📋 Plan generated:", planResponse);

            setCurrentPlan(planResponse.plan);
            setOriginalPlan(JSON.parse(JSON.stringify(planResponse.plan)));
            reset();
            setViewState("plan");

        } catch (err) {
            console.error("Failed to generate plan:", err);
            setDataError(err instanceof Error ? err.message : "Failed to generate analysis plan. Please try again.");
        } finally {
            setIsGeneratingPlan(false);
        }
    };

    const handleExecutePlan = async (skipValidation: boolean = false) => {
        const analysisTables = filteredTablesData.length > 0 ? filteredTablesData : tablesData;
        if (!currentPlan || analysisTables.length === 0) {
            setDataError("Missing plan or table data. Please try again.");
            return;
        }

        setDataError("");
        setViewState("analyzing");

        try {
            console.log("📊 Executing RCA analysis with plan:", currentPlan);
            console.log("📊 Using", analysisTables.length, "table(s)");
            console.log("📋 Original plan provided:", originalPlan ? "yes" : "no");
            console.log("⏭️ Skip validation:", skipValidation);

            await startAnalysis({
                tables: analysisTables,
                user_question: userQuestion,
                plan: currentPlan,
                original_plan: originalPlan || undefined,
                skip_validation: skipValidation || undefined
            });

        } catch (err) {
            console.error("Failed to execute analysis:", err);
            setDataError(err instanceof Error ? err.message : "Failed to execute analysis. Please try again.");
            setViewState("plan");
        }
    };

    const handleRegenerate = () => {
        // Go back to input view to start fresh
        setCurrentPlan(null);
        setOriginalPlan(null);
        setTablesData([]);
        setFilteredTablesData([]);
        setSelectedSheets([""]);
        setTableSemantics([]);
        setIsLoadingDescriptions(false);
        reset(); // Reset progress/error state
        setViewState("input");
    };

    const handlePlanReorder = (reorderedSteps: PlanStep[]) => {
        if (!currentPlan) return;

        console.log("📋 Plan reordered:", reorderedSteps.map(s => s.step_id));

        setCurrentPlan({
            ...currentPlan,
            steps: reorderedSteps
        });
    };

    const handleReset = () => {
        reset();
        setDataError("");
        setCurrentPlan(null);
        setOriginalPlan(null);
        setTablesData([]);
        setFilteredTablesData([]);
        setSelectedSheets([""]);
        setTableSemantics([]);
        setIsLoadingDescriptions(false);
        setViewState("input");
    };

    const handlePlanChange = (updatedPlan: RCAPlan) => {
        console.log("📝 Plan updated with edited reasons:", updatedPlan);
        setCurrentPlan(updatedPlan);
        // Clear error when user makes changes (reset clears the error from useRCAProgress)
        if (error) {
            reset();
        }
    };

    // Show results view when analysis is complete
    if (result) {
        return (
            <RCAResultsView
                result={result}
                userQuestion={userQuestion}
                onReset={handleReset}
                plan={currentPlan}
            />
        );
    }

    // Show column semantics view
    if (viewState === "columns" && tablesData.length > 0) {
        return (
            <ColumnSemanticsView
                tables={tableSemantics}
                onSemanticsChange={setTableSemantics}
                onGeneratePlan={handleGeneratePlan}
                isGeneratingPlan={isGeneratingPlan}
                userQuestion={userQuestion}
                onSelectedColumnsChange={setSelectedColumnKeys}
            />
        );
    }

    // Show plan view when plan is generated or analyzing
    // v2.3.0: Use error from useRCAProgress for validation errors (validation now happens in backend)
    if ((viewState === "plan" || viewState === "analyzing") && currentPlan && tablesData.length > 0) {
        return (
            <RCAPlanView
                plan={currentPlan}
                userQuestion={userQuestion}
                tableData={filteredTablesData.length > 0 ? { headers: filteredTablesData[0].headers, rows: filteredTablesData[0].rows } : { headers: tablesData[0]?.headers || [], rows: tablesData[0]?.rows || [] }}
                onExecute={handleExecutePlan}
                onRegenerate={handleRegenerate}
                onPlanReorder={handlePlanReorder}
                isExecuting={isLoading}
                progress={progress}
                progressMessage={message}
                onPlanChange={handlePlanChange}
                validationError={error || undefined}
            />
        );
    }

    return (
        <>
            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .bg-gradient-custom {
                    background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
                }
                .bg-white-90 {
                    background-color: rgba(255, 255, 255, 0.6);
                }
                .dropdown-scroll {
                    overflow-y: auto;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .dropdown-scroll::-webkit-scrollbar {
                    display: none;
                }
                .textarea-scroll {
                    overflow-y: auto;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .textarea-scroll::-webkit-scrollbar {
                    display: none;
                }
                .main-scroll {
                    overflow-y: auto;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .main-scroll::-webkit-scrollbar {
                    display: none;
                }
                textarea::placeholder {
                    color: #9CA3AF;
                    opacity: 0.7;
                }
                `}
            </style>
            <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
                <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
                    style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
                <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
                    style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

                <SpreetailHeader />

                <div className="flex flex-col items-center p-5 flex-1 main-scroll">
                    <div className="w-full max-w-md">
                        <div className="text-left mb-5">
                            <h1 className="text-lg font-medium leading-none text-gray-800 m-0 mb-3">
                                Analysis
                            </h1>

                            <p className="text-[13px] text-gray-600 mb-4">
                                Identify the underlying causes of trends, anomalies, or issues in your data using AI-powered analysis.
                            </p>
                        </div>

                        {/* Data Source Selection */}
                        <div className="mb-4 w-full">
                            <label className="text-[13px] font-semibold text-gray-800 mb-1.5 block">
                                Data Source
                            </label>

                            <p className="text-[13px] text-gray-500 m-0 mb-3">
                                Select sheets to analyze (up to 4)
                            </p>

                            {isLoadingOptions && (
                                <div className="bg-blue-50 rounded-md p-2 px-3 mb-4 flex items-center gap-2">
                                    <div className="w-[18px] h-[18px] border-2 border-blue-500 border-t-transparent rounded-full"
                                        style={{ animation: 'spin 1s linear infinite' }}></div>
                                    <p className="text-xs text-blue-800 m-0">
                                        Loading available sheets...
                                    </p>
                                </div>
                            )}

                            {showValidation && !selectedSheets.some(s => s.trim()) && (
                                <div className="text-[11px] text-red-600 mb-1.5">
                                    Please select at least one sheet
                                </div>
                            )}

                            {selectedSheets.map((sheet, index) => (
                                <div key={index} className="relative mb-3 flex items-start gap-2">
                                    <div className="flex-1">
                                        <button
                                            onClick={() => {
                                                if (dropdownOpen !== index) { loadSheetOptions(); }
                                                setDropdownOpen(dropdownOpen === index ? null : index);
                                            }}
                                            className="w-full p-2.5 px-3 border border-gray-200 rounded-md bg-white-90 text-[13px] text-left cursor-pointer flex justify-between items-center overflow-hidden"
                                            style={{ color: sheet ? '#1f2937' : '#9CA3AF' }}
                                            title={sheet || "Select sheet"}
                                            disabled={isLoadingOptions}
                                        >
                                            <span className="truncate mr-2">{sheet || "Select sheet"}</span>
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                className={`transition-transform duration-200 flex-shrink-0 ${dropdownOpen === index ? 'rotate-180' : 'rotate-0'}`}
                                            >
                                                <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>

                                        {dropdownOpen === index && (
                                            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-md z-10 mt-1 max-h-48 dropdown-scroll">
                                                {getAvailableOptions(index).length === 0 ? (
                                                    <div className="p-2.5 px-3 text-[13px] text-gray-500 text-center">
                                                        No data sheets found
                                                    </div>
                                                ) : (
                                                    getAvailableOptions(index).map((option, optIdx) => (
                                                        <button
                                                            key={optIdx}
                                                            onClick={() => handleSheetSelect(index, option)}
                                                            className="w-full p-2.5 px-3 border-none bg-transparent text-[13px] text-gray-800 text-left cursor-pointer hover:bg-gray-50 overflow-hidden"
                                                            style={{
                                                                borderBottom: optIdx < getAvailableOptions(index).length - 1 ? "1px solid #F3F4F6" : "none"
                                                            }}
                                                            title={option}
                                                        >
                                                            <span className="truncate block">{option}</span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Remove button - only show if more than 1 row */}
                                    {selectedSheets.length > 1 && (
                                        <button
                                            onClick={() => handleRemoveTable(index)}
                                            className="mt-1 p-1.5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer bg-transparent border-none"
                                            title="Remove table"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            ))}

                            {/* Add Table button */}
                            {selectedSheets.length < 4 && selectedSheets[0] && (
                                <button
                                    onClick={handleAddTable}
                                    className="w-full p-2 border border-dashed border-gray-300 rounded-md text-[13px] text-gray-500 hover:text-[#068E75] hover:border-[#068E75] transition-colors cursor-pointer bg-transparent flex items-center justify-center gap-1.5"
                                >
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    Add Table
                                </button>
                            )}
                        </div>

                        {/* Analysis Question - Card Container */}
                        <div className="mb-4 w-full rounded-lg p-4 border border-gray-200" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                            <label className="text-[13px] font-semibold text-gray-800 mb-1 block">
                                Analysis question
                            </label>

                            <p className="text-[12px] text-gray-500 m-0 mb-3">
                                Describe what you want to investigate
                            </p>

                            {showValidation && !userQuestion.trim() && (
                                <div className="text-[11px] text-red-600 mb-1.5">
                                    Please describe your analysis question
                                </div>
                            )}

                            <textarea
                                value={userQuestion}
                                onChange={(e) => setUserQuestion(e.target.value)}
                                disabled={isLoading}
                                placeholder="Eg: What are our top performing products?"
                                className={`w-full h-[100px] p-3 border-none rounded-md bg-white text-[13px] resize-none outline-none box-border textarea-scroll ${
                                    isLoading ? 'text-gray-500 bg-gray-50' : 'text-gray-800'
                                }`}
                            />
                        </div>

                        {/* Error Display */}
                        {(error || dataError) && (
                            <div ref={errorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
                                <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white text-sm font-bold">!</span>
                                </div>
                                <p className="text-[13px] text-red-800 m-0 leading-snug">{error || dataError}</p>
                            </div>
                        )}

                        {/* Progress Bar - Shows when analyzing */}
                        {(isLoading || viewState === "analyzing") && (
                            <div className="flex-row items-center pb-4 w-full">
                                <ProgressBar
                                    height="15px"
                                    bgColor="#068E75"
                                    labelSize="10px"
                                    completed={Math.round(progress)}
                                    maxCompleted={100}
                                    width="100%"
                                />
                            </div>
                        )}

                    </div>
                </div>

                {/* Footer Section with Next Button */}
                <div className="w-full px-5 py-3" style={{ backgroundColor: 'rgba(211, 232, 217, 0.5)' }}>
                    <div className="w-full max-w-md mx-auto">
                        <button
                            onClick={handleNext}
                            disabled={isLoading || isLoadingDescriptions || isLoadingOptions}
                            className={`w-full text-white border-none rounded-lg py-3 px-4 text-[14px] font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                                isLoading || isLoadingDescriptions
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-[#068E75] cursor-pointer hover:bg-[#057A63]'
                            }`}
                        >
                            {isLoadingDescriptions ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                                        style={{ animation: 'spin 1s linear infinite' }}></div>
                                    Analyzing Columns...
                                </>
                            ) : (
                                'Next'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RootCauseAnalysis;
